/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import i18n from "i18n/i18n";
import { getDateLocaleConfig } from "i18n/locale";

import "../ThermographicInspectionRecord/ThermographicInspectionRecordReport.css";
import "./InspectionRecordReport.css";

import logo from "assets/img/icons/logoTech.png";

import { handleRenderNodeExtras, rowStyle } from "pages/thermography/InspectionRecord/treeRules";
import { getRecordById } from "pages/thermography/InspectionRecord/features/inspectionRecordSlice";

import ReportTemplateBase, { ReportBlock } from "../Templates/ReportTemplateBase";
import { TreeNodeRoute } from "pages/thermography/components/InspectionRouteTree/types/treeNode";

import { EquipmentInspectionStatus } from "pages/thermography/InspectionRecord/utils/enumEquipmentInspectionStatus";
import { ConditionType } from "pages/thermography/model/enumerations/condition-type.model";

import ThermographicInspectionRecordReport from "../ThermographicInspectionRecord/ThermographicInspectionRecordReport";
import { DatabaseService } from "pages/thermography/stores/DatabaseService";
import { AnomalyRecord } from "data/entities/AnomalyRecord";

type Noop = (...args: any[]) => void;
type FlatNode = { node: TreeNodeRoute; depth: number };

export enum TreeNodeType {
    GROUP = "group",
    SUBGROUP = "subgroup",
    ELEMENT = "element",
    ITEM = "item",
}

export const TreeNodeTypeLabel: Record<TreeNodeType, string> = {
    [TreeNodeType.GROUP]: i18n.t("pages.thermography.inspectionRecordReport.treeNodeType.group", { ns: "translation" }),
    [TreeNodeType.SUBGROUP]: i18n.t("pages.thermography.inspectionRecordReport.treeNodeType.subgroup", { ns: "translation" }),
    [TreeNodeType.ELEMENT]: i18n.t("pages.thermography.inspectionRecordReport.treeNodeType.element", { ns: "translation" }),
    [TreeNodeType.ITEM]: i18n.t("pages.thermography.inspectionRecordReport.treeNodeType.item", { ns: "translation" }),
};

const TYPE_INDENT_LEVEL: Record<string, number> = {
    [TreeNodeType.GROUP]: 0,
    [TreeNodeType.SUBGROUP]: 1,
    [TreeNodeType.ELEMENT]: 2,
    [TreeNodeType.ITEM]: 3,
};

type TypeKey = TreeNodeType;

function flattenTree(roots: TreeNodeRoute[]): FlatNode[] {
    const out: FlatNode[] = [];
    const stack: Array<{ node: TreeNodeRoute; depth: number }> = [];

    for (let i = roots.length - 1; i >= 0; i--) {
        stack.push({ node: roots[i], depth: 0 });
    }

    while (stack.length) {
        const cur = stack.pop()!;
        out.push(cur);

        const children = cur.node.children ?? [];
        for (let i = children.length - 1; i >= 0; i--) {
            stack.push({ node: children[i], depth: cur.depth + 1 });
        }
    }

    return out;
}

function normalizeTypeKey(t: any): TypeKey | null {
    const s = String(t ?? "").trim();
    const lower = s.toLowerCase();

    if (
        lower === TreeNodeType.GROUP ||
        lower === TreeNodeType.SUBGROUP ||
        lower === TreeNodeType.ELEMENT ||
        lower === TreeNodeType.ITEM
    ) {
        return lower as TypeKey;
    }

    if ((TreeNodeType as any)[s]) {
        const v = (TreeNodeType as any)[s];
        if (typeof v === "string") return v as TypeKey;
    }

    return null;
}

function parseCsv(raw: string | null): string[] {
    if (!raw) return [];
    return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

function parseStatusSet(raw: string | null): Set<EquipmentInspectionStatus> {
    const allowed = new Set(Object.values(EquipmentInspectionStatus));
    const out = new Set<EquipmentInspectionStatus>();

    parseCsv(raw).forEach((v) => {
        if (allowed.has(v as EquipmentInspectionStatus)) out.add(v as EquipmentInspectionStatus);
    });

    return out;
}

type CondKey = ConditionType;

function toConditionType(v: any): CondKey | null {
    if (!v) return null;

    if (Object.values(ConditionType).includes(v)) return v as CondKey;

    const s = String(v).trim();
    if (Object.values(ConditionType).includes(s as any)) return s as CondKey;

    const byLabel: Record<string, CondKey> = {
        Normal: ConditionType.NORMAL,
        "Baixo Risco": ConditionType.LOW_RISK,
        "Médio Risco": ConditionType.MEDIUM_RISK,
        "Alto Risco": ConditionType.HIGH_RISK,
        "Alto Risco Iminente": ConditionType.IMMINENT_HIGH_RISK,
    };

    if (byLabel[s]) return byLabel[s];

    const upper = s.toUpperCase();
    if (upper === "MEDIO RISCO" || upper === "MÉDIO RISCO") return ConditionType.MEDIUM_RISK;
    if (upper === "ALTO RISCO IMINENTE" || upper === "IMINENTE") return ConditionType.IMMINENT_HIGH_RISK;

    return null;
}

function parseCondSet(raw: string | null): Set<CondKey> {
    const out = new Set<CondKey>();
    parseCsv(raw).forEach((v) => {
        const c = toConditionType(v);
        if (c) out.add(c);
    });
    return out;
}

function getNodeStatus(node: any): EquipmentInspectionStatus | null {
    return (
        node?.status ??
        node?.statusInspection ??
        node?.equipmentStatus ??
        node?.nodeStatus ??
        node?.inspectionStatus ??
        node?.statusInfo ??
        null
    );
}

function getNodeConditionType(node: any): CondKey | null {
    const direct =
        node?.conditionType ??
        node?.condition ??
        node?.status?.conditionType ??
        node?.status?.condition ??
        node?.anomaly?.conditionType ??
        node?.anomaly?.condition ??
        null;

    return toConditionType(direct);
}

function buildVisibilityMapStatusAndCond(
    roots: TreeNodeRoute[] = [],
    selectedStatuses: Set<EquipmentInspectionStatus>,
    selectedConds: Set<CondKey>
) {
    const hasStatusInSubtree = new Map<string, boolean>();
    const hasCondInSubtree = new Map<string, boolean>();
    const visible = new Map<string, boolean>();

    const post = (n: any): { hs: boolean; hc: boolean } => {
        const children = n?.children ?? [];

        const selfStatusHit = (() => {
            const st = getNodeStatus(n);
            return st ? selectedStatuses.has(st) : false;
        })();

        const selfCondHit = (() => {
            const c = getNodeConditionType(n);
            return c ? selectedConds.has(c) : false;
        })();

        let hs = selfStatusHit;
        let hc = selfCondHit;

        for (const ch of children) {
            const r = post(ch);
            hs = hs || r.hs;
            hc = hc || r.hc;
        }

        if (n?.id) {
            hasStatusInSubtree.set(String(n.id), hs);
            hasCondInSubtree.set(String(n.id), hc);
        }

        return { hs, hc };
    };

    const pre = (n: any, statusScope: boolean, condScope: boolean) => {
        const id = String(n?.id ?? "");

        const selfStatusHit = (() => {
            const st = getNodeStatus(n);
            return st ? selectedStatuses.has(st) : false;
        })();

        const selfCondHit = (() => {
            const c = getNodeConditionType(n);
            return c ? selectedConds.has(c) : false;
        })();

        const nextStatusScope = statusScope || selfStatusHit;
        const nextCondScope = condScope || selfCondHit;

        const hs = hasStatusInSubtree.get(id) ?? false;
        const hc = hasCondInSubtree.get(id) ?? false;

        const statusOk = !selectedStatuses.size || nextStatusScope || hs;
        const condOk = !selectedConds.size || nextCondScope || hc;

        const isVisible = statusOk && condOk;
        if (id) visible.set(id, isVisible);

        const children = n?.children ?? [];
        for (const ch of children) {
            pre(ch, nextStatusScope, nextCondScope);
        }
    };

    if (!selectedStatuses.size && !selectedConds.size) {
        const markAll = (n: any) => {
            if (n?.id) visible.set(String(n.id), true);
            (n?.children ?? []).forEach(markAll);
        };
        roots.forEach(markAll);
        return visible;
    }

    for (const r of roots) post(r);
    for (const r of roots) pre(r, false, false);

    return visible;
}

/**
 * Filtro extra por tipo (apenas GROUP e SUBGROUP) com switches.
 * Regras:
 * - Se um tipo estiver desligado, esconde os nós daquele tipo.
 * - Mantém árvore válida: se filhos ainda aparecerem, sobe o "typeDepth" para não ficar com indent quebrado.
 */
function buildVisibilityMapByTypeSwitches(
    roots: TreeNodeRoute[] = [],
    showGroup: boolean,
    showSubgroup: boolean
) {
    const visible = new Map<string, boolean>();

    const isAllowedType = (t: TypeKey | null) => {
        if (!t) return true;
        if (t === TreeNodeType.GROUP) return showGroup;
        if (t === TreeNodeType.SUBGROUP) return showSubgroup;
        // ELEMENT e ITEM sempre continuam existindo (não há switch pra eles)
        return true;
    };

    const walk = (n: any) => {
        const id = String(n?.id ?? "");
        const t = normalizeTypeKey(n?.type);
        if (id) visible.set(id, isAllowedType(t));
        (n?.children ?? []).forEach(walk);
    };

    roots.forEach(walk);
    return visible;
}

function TreeRowReport({
    node,
    depth,
    rowStyleFn,
    renderNodeExtras,
}: {
    node: TreeNodeRoute;
    depth: number;
    rowStyleFn?: (n: TreeNodeRoute) => React.CSSProperties | undefined;
    renderNodeExtras?: (
        n: TreeNodeRoute,
        updateNode: (id: string, updater: (x: TreeNodeRoute) => TreeNodeRoute) => React.ReactNode
    ) => React.ReactNode;
}) {
    const updateNodeNoop = (_id: string, _updater: (x: TreeNodeRoute) => TreeNodeRoute): React.ReactNode => null;

    const isIncluded = node.included ?? false;

    return (
        <div className="inspection-report__tree-row">
            <div
                className={`tree-node tree-${(node as any).type}`}
                style={{
                    marginLeft: depth * 24,
                    marginBottom: 6,
                    padding: "3px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--bs-border-color)",
                    boxShadow: "var(--bs-box-shadow-sm)",
                    transition: "all 160ms ease",
                    opacity: isIncluded ? 1 : 0.45,
                    filter: isIncluded ? "none" : "grayscale(0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    userSelect: "none",
                    ...(rowStyleFn ? rowStyleFn(node) : {}),
                }}
            >
                <span
                    className="tree-node-title"
                    style={{
                        flex: 1,
                        fontWeight: 600,
                        fontSize: 12,
                        color: "var(--phoenix-tertiary-color)",
                        minWidth: 0,
                    }}
                >
                    {node.title}
                </span>

                {renderNodeExtras ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                        {renderNodeExtras(node, updateNodeNoop)}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

type ThermoPayload = {
    anomalyRecord: any | null;
    equipmentsFiltered: any[];
    inspectionRoutesFiltered: any[];
    equipmentType: string;
};

export default function InspectionRecordReport() {
    const dispatch: any = useDispatch();
    const location = useLocation();
    const dbService = DatabaseService.getInstance();
    const { id } = useParams<{ id: string }>();
    const { t, i18n: i18nInstance } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
    const dateLocale = getDateLocaleConfig(i18nInstance.language);
    const dateTimeFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(dateLocale.intlLocale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }),
        [dateLocale.intlLocale]
    );
    const dateTimeWithSecondsFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(dateLocale.intlLocale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }),
        [dateLocale.intlLocale]
    );
    const emissionDateRef = React.useRef<Date>(new Date());
    const emissionValue = useMemo(
        () => dateTimeWithSecondsFormatter.format(emissionDateRef.current),
        [dateTimeWithSecondsFormatter]
    );

    const { data: record, nodes } = useSelector((state: any) => state.inspectionRecord);

    //switches de exibição (por padrão ligados)
    const [showGroup, setShowGroup] = useState(true);
    const [showSubgroup, setShowSubgroup] = useState(true);

    const [showThermo, setShowThermo] = useState(false);
    const [thermoSignatures, setThermoSignatures] = useState<Record<string, { thermographerId: string; managerId: string }>>(
        {}
    );

    const [inspectionPagesCount, setInspectionPagesCount] = useState(1);
    const [thermoPagesByCodigo, setThermoPagesByCodigo] = useState<Record<string, number>>({});

    const [anomalyRecords, setAnomalyRecords] = useState<AnomalyRecord[]>([]);
    const [dbReady, setDbReady] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                if (cancelled) return;

                const records = dbService.getAnomalyRecords() ?? [];
                setAnomalyRecords(records);
                setDbReady(true);
            } catch (e) {
                setDbReady(true);
            }
        };

        init();

        return () => {
            cancelled = true;
        };
    }, []);

    const locationAny = location as any;
    const stateRecord = locationAny.state?.anomalyRecord ?? null;

    const powerStations = useMemo(
        () => dbService.getPowerStations().slice().sort((a: any, b: any) => a.name.localeCompare(b.name)),
        [dbService]
    );

    const equipments = useMemo(() => dbService.getEquipments(), [dbService]);

    const inspectionRoutes = useMemo(
        () => dbService.getInspectionRecords().filter((x: any) => x.finished == false),
        [dbService]
    );

    const thermographers = useMemo(() => dbService.getThermographers(), [dbService]);

    useEffect(() => {
        if (id) dispatch(getRecordById(id));
    }, [id, dispatch]);

    const { selectedStatusesFromUrl, selectedCondsFromUrl } = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return {
            selectedStatusesFromUrl: parseStatusSet(params.get("status")),
            selectedCondsFromUrl: parseCondSet(params.get("cond")),
        };
    }, [location.search]);

    const formatDateTime = (date?: string | null) => {
        if (!date) return "--";
        const parsed = dayjs(date);
        if (!parsed.isValid()) return "--";
        return dateTimeFormatter.format(parsed.toDate());
    };

    const startedByName = record?.startedBy?.name ?? record?.startedBy?.name ?? "--";
    const finishedByName = record?.finishedBy?.name ?? record?.finishedBy?.name ?? "--";
    const hasStarted = !!record?.started;
    const hasFinished = !!record?.finished;

    const noop: Noop = () => { };

    const roots = useMemo(() => ((nodes ?? []) as TreeNodeRoute[]), [nodes]);

    const visibilityMapStatusCond = useMemo(() => {
        return buildVisibilityMapStatusAndCond(roots, selectedStatusesFromUrl, selectedCondsFromUrl);
    }, [roots, selectedStatusesFromUrl, selectedCondsFromUrl]);

    const visibilityMapTypeSwitches = useMemo(() => {
        return buildVisibilityMapByTypeSwitches(roots, showGroup, showSubgroup);
    }, [roots, showGroup, showSubgroup]);

    const visibilityMapFinal = useMemo(() => {
        const out = new Map<string, boolean>();

        // une as chaves de ambos
        const keys = new Set<string>();
        visibilityMapStatusCond.forEach((_v, k) => keys.add(k));
        visibilityMapTypeSwitches.forEach((_v, k) => keys.add(k));

        // se não existir em algum mapa, assume true
        keys.forEach((k) => {
            const a = visibilityMapStatusCond.get(k);
            const b = visibilityMapTypeSwitches.get(k);
            out.set(k, (a ?? true) && (b ?? true));
        });

        return out;
    }, [visibilityMapStatusCond, visibilityMapTypeSwitches]);

    const thermoCodes: string[] = useMemo(() => {
        if (!roots?.length) return [];

        const flat = flattenTree(roots);

        const filtered = flat
            .filter(({ node }) => {
                const idStr = String((node as any)?.id ?? "");
                return visibilityMapFinal.get(idStr) ?? true;
            })
            .filter(({ node }) => normalizeTypeKey((node as any)?.type) === TreeNodeType.ITEM);

        const out: string[] = [];
        const seen = new Set<string>();

        for (const { node } of filtered) {
            const codigo = String((node as any)?.id ?? "").trim();
            if (!codigo) continue;
            if (seen.has(codigo)) continue;
            seen.add(codigo);
            out.push(codigo);
        }

        return out;
    }, [roots, visibilityMapFinal]);

    useEffect(() => {
        if (!showThermo) setThermoPagesByCodigo({});
    }, [showThermo]);

    useEffect(() => {
        setThermoPagesByCodigo((prev) => {
            const next: Record<string, number> = {};
            for (const codigo of thermoCodes) {
                if (prev[codigo] != null) next[codigo] = prev[codigo];
            }
            return next;
        });
    }, [thermoCodes.join("|")]);

    const thermoTotalPages = useMemo(() => {
        if (!showThermo) return 0;
        return thermoCodes.reduce((sum, codigo) => sum + (thermoPagesByCodigo[codigo] ?? 0), 0);
    }, [showThermo, thermoCodes, thermoPagesByCodigo]);

    const globalTotalPages = useMemo(() => {
        return inspectionPagesCount + thermoTotalPages;
    }, [inspectionPagesCount, thermoTotalPages]);

    const thermoByCode = useMemo<Record<string, ThermoPayload>>(() => {
        const out: Record<string, ThermoPayload> = {};

        for (const codigo of thermoCodes) {
            const anomalyRecord =
                stateRecord && String(stateRecord?.id) === String(codigo)
                    ? stateRecord
                    : (anomalyRecords.find((x: any) => String(x.id) === String(codigo)) ?? null);

            if (!anomalyRecord) {
                out[codigo] = { anomalyRecord: null, equipmentsFiltered: [], inspectionRoutesFiltered: [], equipmentType: "" };
                continue;
            }

            const ps = powerStations.find((p: any) => p.id == anomalyRecord.plant?.id);
            const equipmentsFiltered = ps
                ? equipments.filter((e: any) => String(e.code ?? "").includes(String(ps.code ?? "")))
                : [];
            const inspectionRoutesFiltered = ps
                ? inspectionRoutes.filter((r: any) => r.power_station_id == ps.id)
                : [];

            const eq = equipments.find((e: any) => e.id == anomalyRecord.equipment?.id);
            // const equipmentType = eq ? `${String(eq.code).split("-").pop()} (${eq.name})` : "";
            const equipmentType = eq ? `${String(eq.code)}` : "";

            out[codigo] = { anomalyRecord, equipmentsFiltered, inspectionRoutesFiltered, equipmentType };
        }

        return out;
    }, [thermoCodes, stateRecord, anomalyRecords, powerStations, equipments, inspectionRoutes]);

    const inspectionBlocks: ReportBlock[] = useMemo(() => {
        if (!record) return [];

        const headerBlock: ReportBlock = {
            key: "header",
            node: (
                <div className="inspection-report__headerblock">
                    <div className="inspection-report__headline">
                        <div className="inspection-report__headline-left">
                            <h5 className="m-0 fw-normal">
                                <span className="fw-bold">
                                    {tt("pages.thermography.inspectionRecordReport.fields.inspectionRecord")}:
                                </span>{" "}
                                {record.name ?? ""}
                            </h5>
                        </div>

                        <div className="inspection-report__headline-right">
                            <span className="fw-bold small">{tt("pages.thermography.inspectionRecordReport.status.label")}:</span>
                            <span
                                className={`inspection-report__badge ${hasFinished ? "is-finished" : hasStarted ? "is-started" : "is-pending"
                                    }`}
                            >
                                {hasFinished
                                    ? tt("pages.thermography.inspectionRecordReport.status.finished")
                                    : hasStarted
                                        ? tt("pages.thermography.inspectionRecordReport.status.inProgress")
                                        : tt("pages.thermography.inspectionRecordReport.status.notStarted")}
                            </span>
                        </div>
                    </div>

                    <div className="inspection-report__card">
                        <div className="inspection-report__grid">
                            <div className="inspection-report__field">
                                <div className="inspection-report__label">
                                    {tt("pages.thermography.inspectionRecordReport.fields.installation")}
                                </div>
                                <div className="inspection-report__value">{record.plant?.name ?? "--"}</div>
                            </div>

                            <div className="inspection-report__field">
                                <div className="inspection-report__label">
                                    {tt("pages.thermography.inspectionRecordReport.fields.maintenanceDocument")}
                                </div>
                                <div className="inspection-report__value">{record.maintenanceDocument ?? "--"}</div>
                            </div>

                            <div className="inspection-report__field">
                                <div className="inspection-report__label">
                                    {tt("pages.thermography.inspectionRecordReport.fields.expectedEnd")}
                                </div>
                                <div className="inspection-report__value">
                                    {formatDateTime(record.expectedEndDate)}
                                </div>
                            </div>

                            <div className="inspection-report__field inspection-report__field--wide">
                                <div className="inspection-report__label">
                                    {tt("pages.thermography.inspectionRecordReport.fields.recordDescription")}
                                </div>
                                <div className="inspection-report__value">{record.description ?? "--"}</div>
                            </div>
                        </div>

                        {(hasStarted || hasFinished) && (
                            <div className="inspection-report__audit">
                                {hasStarted && (
                                    <div className="inspection-report__audit-item">
                                        <span className="inspection-report__audit-label">
                                            {tt("pages.thermography.inspectionRecordReport.info.start")}:
                                        </span>
                                        <span className="inspection-report__audit-value">{formatDateTime(record.startedAt)}</span>
                                        <span className="inspection-report__audit-muted">
                                            {tt("pages.thermography.inspectionRecordReport.info.by")}
                                        </span>
                                        <span className="inspection-report__audit-value">{startedByName}</span>
                                    </div>
                                )}

                                {hasFinished && (
                                    <div className="inspection-report__audit-item">
                                        <span className="inspection-report__audit-label">
                                            {tt("pages.thermography.inspectionRecordReport.info.finish")}:
                                        </span>
                                        <span className="inspection-report__audit-value">{formatDateTime(record.finishedAt)}</span>
                                        <span className="inspection-report__audit-muted">
                                            {tt("pages.thermography.inspectionRecordReport.info.by")}
                                        </span>
                                        <span className="inspection-report__audit-value">{finishedByName}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ),
        };

        const flat = flattenTree(roots);

        const filteredFlat = flat
            .filter(({ node }) => {
                const idStr = String((node as any)?.id ?? "");
                return visibilityMapFinal.get(idStr) ?? true;
            })
            .map(({ node }) => {
                const t = normalizeTypeKey((node as any)?.type);
                const typeDepth = t ? TYPE_INDENT_LEVEL[t] ?? 0 : 0;
                return { node, depth: typeDepth };
            });

        const treeRowBlocks: ReportBlock[] = filteredFlat.map(({ node, depth }, idx) => ({
            key: `tree-row-${(node as any).id ?? idx}`,
            node: (
                <div className="inspection-report-tree">
                    <TreeRowReport
                        node={node}
                        depth={depth}
                        rowStyleFn={rowStyle}
                        renderNodeExtras={(n) => handleRenderNodeExtras(n as any, noop as any, true, noop as any, noop as any)}
                    />
                </div>
            ),
        }));

        return [headerBlock, ...treeRowBlocks];
    }, [
        record,
        roots,
        hasStarted,
        hasFinished,
        startedByName,
        finishedByName,
        visibilityMapFinal,
        dateTimeFormatter,
        i18nInstance.language,
        t,
    ]);

    if (!record) return <div className="p-4">{tt("pages.thermography.inspectionRecordReport.loading")}</div>;

    return (
        <div className="inspection-report-screen">
            <aside className="inspection-report-screen__filters">
                <div className="inspection-report-filterCard" style={{ position: "relative" }}>
                    <div className="inspection-report-filterCard__header">
                        <div className="inspection-report-filter">{tt("pages.thermography.inspectionRecordReport.filters.title")}</div>
                    </div>

                    <div className="inspection-report-filterCard__body">
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <Form.Check
                                type="switch"
                                id="show-group"
                                label={tt("pages.thermography.inspectionRecordReport.filters.showGroup")}
                                checked={showGroup}
                                onChange={(e) => setShowGroup(e.currentTarget.checked)}
                            />

                            <Form.Check
                                type="switch"
                                id="show-subgroup"
                                label={tt("pages.thermography.inspectionRecordReport.filters.showSubgroup")}
                                checked={showSubgroup}
                                onChange={(e) => setShowSubgroup(e.currentTarget.checked)}
                            />

                            <Form.Check
                                type="switch"
                                id="show-thermo"
                                label={tt("pages.thermography.inspectionRecordReport.filters.includeThermographicRecord")}
                                checked={showThermo}
                                onChange={(e) => setShowThermo(e.currentTarget.checked)}
                            />
                        </div>

                        {showThermo && thermoCodes.length === 0 ? (
                            <div className="d-print-none alert alert-secondary mt-3 mb-0">
                                {tt("pages.thermography.inspectionRecordReport.filters.noThermographicRecordsPrefix")}{" "}
                                <b>{tt("pages.thermography.inspectionRecordReport.treeNodeType.item")}</b>{" "}
                                {tt("pages.thermography.inspectionRecordReport.filters.noThermographicRecordsSuffix")}
                            </div>
                        ) : null}
                    </div>
                </div>
            </aside>

            <div className="inspection-report-screen__paper">
                {/* 1) Relatório de inspeção */}
                <ReportTemplateBase
                    className="thermo-report"
                    title={tt("pages.thermography.inspectionRecordReport.report.title")}
                    headerInfo={{
                        subtitle: "",
                        company: {
                            name: "TECH Monitoramento da Condição LTDA.",
                            cnpj: "63.153.215/0001-62",
                            address: "Rua Dezesseis de Março 415, Sala 805, Centro, Petrópolis - RJ",
                        }, docBox: [
                            { label: tt("pages.thermography.inspectionRecordReport.report.docBox.emission"), value: emissionValue },
                            { label: tt("pages.thermography.inspectionRecordReport.fields.installation"), value: record.plant?.name },
                            { label: tt("pages.thermography.inspectionRecordReport.report.docBox.inspectionRecord"), value: record.name },
                        ],
                    }}
                    logoSrc={logo}
                    logoWidth={170}
                    blocks={inspectionBlocks}
                    pageNumberStart={1}
                    totalPages={globalTotalPages}
                    onPagesCount={(count) => setInspectionPagesCount(count)}
                />

                {/* 2) Termográficos */}
                {showThermo
                    ? thermoCodes.map((codigo, idx) => {
                        const prevThermoPages = thermoCodes
                            .slice(0, idx)
                            .reduce((sum, c) => sum + (thermoPagesByCodigo[c] ?? 0), 0);

                        const start = inspectionPagesCount + prevThermoPages + 1;
                        const sig = thermoSignatures[codigo] ?? { thermographerId: "", managerId: "" };
                        const payload = thermoByCode[codigo];

                        const onThermographerChange = (v: string) =>
                            setThermoSignatures((prev) => ({
                                ...prev,
                                [codigo]: { ...(prev[codigo] ?? { managerId: "" }), thermographerId: v },
                            }));

                        const onManagerChange = (v: string) =>
                            setThermoSignatures((prev) => ({
                                ...prev,
                                [codigo]: { ...(prev[codigo] ?? { thermographerId: "" }), managerId: v },
                            }));

                        return (
                            <div key={codigo} className="thermo-section">
                                {!payload?.anomalyRecord ? (
                                    <div className="alert alert-warning" style={{ margin: 0 }}>
                                        {tt("pages.thermography.inspectionRecordReport.thermographicRecordNotFound")}
                                    </div>
                                ) : (
                                    <ThermographicInspectionRecordReport
                                        codigo={codigo}
                                        anomalyRecord={payload.anomalyRecord}
                                        powerStations={powerStations}
                                        equipmentsFiltered={payload.equipmentsFiltered}
                                        inspectionRoutesFiltered={payload.inspectionRoutesFiltered}
                                        thermographers={thermographers}
                                        equipmentType={payload.equipmentType}
                                        thermographerId={sig.thermographerId}
                                        managerId={sig.managerId}
                                        onThermographerChange={onThermographerChange}
                                        onManagerChange={onManagerChange}
                                        pageNumberStart={start}
                                        totalPages={globalTotalPages}
                                        onPagesCount={(count) =>
                                            setThermoPagesByCodigo((prev) => ({ ...prev, [codigo]: count }))
                                        }
                                    />
                                )}
                            </div>
                        );
                    })
                    : null}
            </div>
        </div>
    );
}
